import React, {
  ChangeEvent,
  FC,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { finalize } from 'rxjs'

import {
  Close,
  DeleteOutlineRounded,
  FolderOpenRounded,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { GorillaDialog } from '@procom-labs/atoms'
import { useSubjectSelector } from '@procom-labs/common'
import { AlertContext, TextHookField } from '@procom-labs/molecules'

import { ReportProblemErrorMapping } from '@resume-portal/models'
import { reportProblemService } from '@resume-portal/services'
import {
  resumeBuilderStore,
  userOrganizationStore,
} from '@resume-portal/stores'

import { FormAlert } from './ui/form-alert'

interface ConfirmActionDialogProps {
  open: boolean
  onClose: () => void
}

export const ReportProblemDialog: FC<ConfirmActionDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('main')
  const { userOrganization } = useSubjectSelector(userOrganizationStore, [
    'userOrganization',
  ])
  const form = useForm({
    defaultValues: {
      description: '',
    },
    mode: 'onChange',
  })
  const { handleSubmit, getValues, reset } = form
  const { addAlert } = useContext(AlertContext)
  const { file } = useSubjectSelector(resumeBuilderStore, ['file'])
  const [additionalFiles, setAdditionalFiles] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showAlert, setShowAlert] = useState(false)

  const handleUploadedFilesChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      if (e.target.files) {
        const uploadedFiles = Array.from(e.target.files)
        setAdditionalFiles([...additionalFiles, ...uploadedFiles])
      }
    },
    [additionalFiles]
  )

  const handleRemoveFile = useCallback(
    (index: number): void => {
      setAdditionalFiles(additionalFiles.filter((_, i) => i !== index))
    },
    [additionalFiles]
  )

  const onSubmit = useCallback((): void => {
    if (!userOrganization?.id) return

    setIsSubmitting(true)
    setShowAlert(false)
    setErrorMessage('')

    const formValues = getValues()

    const payload = {
      organizationId: userOrganization?.id,
      description: formValues.description,
      resume: file,
      files: additionalFiles,
    }

    reportProblemService
      .reportProblem(payload)
      .pipe(
        finalize(() => {
          setIsSubmitting(false)
        })
      )
      .subscribe({
        next: () => {
          addAlert({
            message: t('reportProblem.success'),
            severity: 'success',
          })
          onClose()
        },
        error: (err) => {
          let errMessage = err?.response?.data?.message
          errMessage =
            t(ReportProblemErrorMapping[errMessage]) ?? t('reportProblem.error')
          setErrorMessage(errMessage)
          setShowAlert(true)
        },
      })
  }, [
    addAlert,
    additionalFiles,
    file,
    getValues,
    onClose,
    t,
    userOrganization?.id,
  ])

  useEffect(() => {
    if (open) {
      reset()
      setAdditionalFiles([])
      setErrorMessage('')
      setShowAlert(false)
    }
  }, [open, reset])

  return (
    <GorillaDialog
      open={open}
      onClose={onClose}
      closeAfterTransition={false}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          width: '700px',
        },
      }}
      maxWidth="md"
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Stack>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              variant="h5Bold700"
              sx={{ fontSize: '20px' }}
              fontFamily="Manrope"
              color="gorillaResume.text.primary"
            >
              {t('profileMenu.report')}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'gorillaResume.text.secondary',
              ml: '4px',
            }}
          >
            {t('reportProblem.subtitle')}
          </Typography>
        </Stack>
        <Divider sx={{ mt: '8px' }} />
        {showAlert && (
          <Box sx={{ mt: '12px' }}>
            <FormAlert
              onClose={() => setShowAlert(false)}
              alertText={errorMessage}
            />
          </Box>
        )}
      </DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ pt: 1 }}>
            <Stack gap={3}>
              {file && (
                <Stack>
                  <Typography
                    variant="subtitle2"
                    fontSize="14px"
                    fontFamily="Manrope"
                    color="black"
                  >
                    {t('reportProblem.resume')}
                  </Typography>
                  <Box
                    sx={{
                      height: '37px',
                      backgroundColor: 'common.glass',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography fontSize="14px" sx={{ ml: '16px' }}>
                      {file.name}
                    </Typography>
                  </Box>
                </Stack>
              )}
              <Stack gap={4}>
                <Stack>
                  <Typography
                    variant="subtitle2"
                    fontSize="14px"
                    fontFamily="Manrope"
                    color="black"
                  >
                    {t('reportProblem.describeProblem')}
                  </Typography>
                  <Stack sx={{ pl: '4px' }} gap={1}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'gorillaResume.text.secondary',
                        ml: '4px',
                      }}
                    >
                      {t('reportProblem.describeProblemSubtitle')}
                    </Typography>
                    <TextHookField
                      name="description"
                      required
                      control={form.control}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          fontSize: '14px',
                          '& fieldset': {
                            borderColor: 'gorillaResume.outline',
                          },
                          '&:hover fieldset': {
                            borderColor: 'gorillaResume.outline',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'gorillaResume.outline',
                          },
                        },
                      }}
                      multiline
                      rows={6}
                    />
                  </Stack>
                </Stack>
                <Stack gap={1}>
                  <Typography
                    variant="h5Bold700"
                    sx={{ fontSize: '15px' }}
                    fontFamily="Manrope"
                    color="gorillaResume.text.primary"
                  >
                    {t('reportProblem.additionalFiles')}
                  </Typography>
                  <Stack sx={{ pl: '4px' }} gap={1}>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'gorillaResume.text.secondary',
                      }}
                    >
                      {t('reportProblem.additionalFilesSubtitle')}
                    </Typography>
                    {additionalFiles.map((additionalFile, index) => (
                      <Box
                        key={additionalFile.name}
                        sx={{
                          height: '37px',
                          backgroundColor: 'common.glass',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                            px: 1,
                          }}
                          key={additionalFile.name}
                        >
                          <Typography fontSize="14px" sx={{ ml: 1 }}>
                            {additionalFile.name}
                          </Typography>
                          <IconButton
                            onClick={() => handleRemoveFile(index)}
                            sx={{
                              width: '30px',
                              height: '30px',
                              '&:hover': {
                                backgroundColor: '#F6E6E6',
                              },
                            }}
                          >
                            <DeleteOutlineRounded
                              sx={{
                                fontSize: '24px',
                                color: 'gorillaResume.error.dark',
                              }}
                            />
                          </IconButton>
                        </Stack>
                      </Box>
                    ))}
                    <Button
                      variant="outlined"
                      sx={{
                        maxWidth: 'fit-content',
                        height: '40px',
                        textTransform: 'none',
                        borderRadius: '24px',
                        color: 'gorillaResume.button',
                      }}
                      component="label"
                      startIcon={<FolderOpenRounded />}
                    >
                      <Typography fontWeight="500" fontSize="14px">
                        {t('reportProblem.upload')}
                      </Typography>
                      <input
                        type="file"
                        accept="image/*,video/*,.pdf,.doc,.docx"
                        multiple
                        onChange={handleUploadedFilesChange}
                        hidden
                      />
                    </Button>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                height: '40px',
                borderRadius: '24px',
                textTransform: 'none',
              }}
            >
              {t('reportProblem.cancel')}
            </Button>
            <LoadingButton
              loading={isSubmitting}
              type="submit"
              variant="contained"
              sx={{
                height: '40px',
                borderRadius: '24px',
                textTransform: 'none',
                backgroundColor: 'common.purple',
              }}
            >
              {t('reportProblem.submit')}
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </GorillaDialog>
  )
}
